import { Card, CardHeader, Stack } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import type { AgBarSeriesOptions } from 'ag-charts-enterprise';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import VerticalBarChart from '@/components/charts/VerticalBarChart';
import { REPORTS_COLORS } from '@/constants/chartcolors.constants';
import { type AssetAlertsDTO, useFetchAssetAlertsPerDay } from '@/pages/Assets';

import { AssetAlertsTable } from '../AssetAlertsTable';
import useStyles from './AssetAlerts.styles';

const AssetAlerts = () => {
  const { mode: themeMode } = useColorScheme();
  const { classes } = useStyles({ themeMode });

  const { data: selectedAssetAlertsPerDay } = useFetchAssetAlertsPerDay();

  const alertsBarSeries: AgBarSeriesOptions[] = useMemo(() => {
    return [
      {
        type: 'bar',
        xKey: 'date',
        yKey: 'alerts',
        yName: 'Alerts',
        fill: REPORTS_COLORS.CASES
      }
    ];
  }, []);

  const alertsBarData = useMemo(() => {
    const startDate = dayjs();
    const endDate = startDate.subtract(30, 'days');

    // Create a map of existing alert data by date
    const alertsMap = new Map();
    selectedAssetAlertsPerDay?.forEach((item: AssetAlertsDTO) => {
      if (item.created_at) {
        const dateKey = dayjs(item.created_at).format('YYYY-MM-DD');
        // Set count for given date
        alertsMap.set(dateKey, item.count || 0);
      }
    });

    // Generate array with data for every date between endDate and startDate (inclusive)
    const result = [];
    let currentDate = endDate;

    while (currentDate.isSameOrBefore(startDate, 'day')) {
      const dateKey = currentDate.format('YYYY-MM-DD');
      result.push({
        date: currentDate.toDate(),
        alerts: alertsMap.get(dateKey) || 0
      });
      currentDate = currentDate.add(1, 'day');
    }

    return result;
  }, [selectedAssetAlertsPerDay]);

  return (
    <Stack
      direction="column"
      spacing={0}
      className={classes.assetAlertsContainer}
    >
      <Card>
        <CardHeader
          title="Asset Alerts"
          slotProps={{
            title: { className: classes.assetAlertsContainerHeader }
          }}
        />
        <div className={classes.assetAlertsTableWrapper}>
          <VerticalBarChart
            data={alertsBarData}
            series={alertsBarSeries as AgBarSeriesOptions[]}
            height={200}
          />
        </div>
        <AssetAlertsTable />
      </Card>
    </Stack>
  );
};

export default AssetAlerts;
